import React from 'react';
import { Mold } from '../../types/mold';
import TableComponent from '../shared/TableComponent';
import TableCell, { ProductPartsCell, SelectCell } from '../MoldList/TableCell';
import { ActionCell, GenericColumn } from '../KanbanBoard/utils/tableColumns';
import CustomerCodeLabel from '../shared/CustomerCodeLabel';
import MoldStatus from '../shared/MoldStatus';
import MoldShotProgressGradient from '../shared/MoldShotProgressGradient';
import { useAppState } from '@/context/AppStateContext';
import { ClientCompanyCode } from '../../models/appState';
import { applyMoldColumnConfiguration } from '../KanbanBoard/utils/columnConfigurations';
import { useNavigate } from 'react-router-dom';

interface MoldTableViewProps {
  molds: Mold[];
  visibleColumns: Record<string, boolean>;
  selectedMolds: string[];
  onSelect: (moldId: string) => void;
  onSelectAll: (selected: boolean) => void;
  pagination?: boolean;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onDataChange: (data: Mold[]) => void;
}

export const getMoldTableColumns = ({
  getStatusText,
  companyCode
}: {
  getStatusText?: (mold: Mold) => string;
  companyCode?: ClientCompanyCode;
} = {}): GenericColumn<Mold>[] => {
  const allColumns: GenericColumn<Mold>[] = [
    // ── ข้อมูลทั่วไป ───────────────────────────────────────────────────
    {
      key: 'sbiId',
      label: 'รหัสแม่พิมพ์',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{mold.sbiId}</div>,
      filterOptions: (molds) => [...new Set(molds.map(mold => mold.sbiId))],
      getFilterValue: (mold) => mold.sbiId,
      toCSV: (mold) => mold.sbiId
    },
    {
      key: 'moldCode',
      label: 'Mold Code',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{mold.moldCode ?? '-'}</div>,
      filterOptions: (molds) => [...new Set(molds.map(mold => mold.moldCode))],
      getFilterValue: (mold) => mold.moldCode,
      toCSV: (mold) => mold.moldCode
    },
    {
      key: 'moldName',
      label: 'Mold Name',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{mold.moldName ?? '-'}</div>,
      filterOptions: (molds) => [...new Set(molds.map(mold => mold.moldName))],
      getFilterValue: (mold) => mold.moldName,
      toCSV: (mold) => mold.moldName
    },
    {
      key: 'moldNumber',
      label: 'แม่พิมพ์ลูกที่',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{mold.moldNumber}</div>,
      filterOptions: (molds) => [...new Set(molds.map(mold => mold.moldNumber))],
      getFilterValue: (mold) => mold.moldNumber,
      toCSV: (mold) => mold.moldNumber
    },
    {
      key: 'customer',
      label: 'ลูกค้า',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: true,
      render: (mold) => <CustomerCodeLabel customer={mold.customer} />,
      filterOptions: (molds) => [...new Set(molds.map(mold => mold.customer.code))],
      getFilterValue: (mold) => mold.customer.code,
      toCSV: (mold) => mold.customer.code
    },
    {
      key: 'productParts',
      label: 'รหัสชิ้นงาน',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: true,
      render: (mold) => (
        <ProductPartsCell parts={mold.productParts} />
      ),
      filterOptions: (molds) => {
        const parts = molds.flatMap(mold =>
          mold.productParts.map(part => part.partNumber)
        );
        return [...new Set(parts)];
      },
      getFilterValue: (mold) => mold.productParts.map(p => p.partNumber).join(','),
      toCSV: (mold) => `"${mold.productParts.map(p => p.partNumber.replace(/"/g, '""')).join(', ')}"`,
    },
    {
      key: 'status',
      label: 'สถานะ',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: true,
      render: (mold) => <MoldStatus mold={mold} />,
      filterOptions: (molds) => [...new Set(molds.map(mold => {
        if (getStatusText) {
          return getStatusText(mold);
        }
        return mold.status
      }))],
      getFilterValue: (mold) => {
        if (getStatusText) {
          return getStatusText(mold);
        }
        return mold.status
      },
      toCSV: (mold) => mold.status
    },
    {
      key: 'revision',
      label: 'Revision',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: false,
      render: (mold) => <div>{mold.revision ?? '-'}</div>,
      getFilterValue: (mold) => mold.revision?.toString() ?? '',
      toCSV: (mold) => mold.revision?.toString() ?? ''
    },
    {
      key: 'moldType',
      label: 'ประเภทของแม่พิมพ์',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{mold.moldType ?? '-'}</div>,
      filterOptions: (molds) => [...new Set(molds.map(mold => mold.moldType).filter(Boolean))],
      getFilterValue: (mold) => mold.moldType ?? '',
      toCSV: (mold) => mold.moldType ?? ''
    },
    {
      key: 'moldSource',
      label: 'Mold Source',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{(mold as any).moldSource ?? '-'}</div>,
      filterOptions: (molds) => [...new Set(molds.map(mold => (mold as any).moldSource).filter(Boolean))],
      getFilterValue: (mold) => (mold as any).moldSource ?? '',
      toCSV: (mold) => (mold as any).moldSource ?? ''
    },
    {
      key: 'cavity',
      label: 'Cavity',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{mold.cavity ?? '-'}</div>,
      filterOptions: (molds) => [...new Set(molds.map(mold => mold.cavity).filter(Boolean))],
      getFilterValue: (mold) => mold.cavity ?? '',
      toCSV: (mold) => mold.cavity ?? ''
    },
    {
      key: 'makerName',
      label: 'Mold Maker Name',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{(mold as any).makerName ?? '-'}</div>,
      filterOptions: (molds) => [...new Set(molds.map(mold => (mold as any).makerName).filter(Boolean))],
      getFilterValue: (mold) => (mold as any).makerName ?? '',
      toCSV: (mold) => (mold as any).makerName ?? ''
    },
    {
      key: 'country',
      label: 'Country',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{mold.extraAttributes?.['country'] ?? '-'}</div>,
      filterOptions: (molds) => [...new Set(molds.map(mold => mold.extraAttributes?.['country']).filter(Boolean))],
      getFilterValue: (mold) => mold.extraAttributes?.['country'] ?? '',
      toCSV: (mold) => mold.extraAttributes?.['country'] ?? ''
    },
    {
      key: 'plate',
      label: 'Plate',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{mold.plate || '-'}</div>,
      filterOptions: (molds) => [...new Set(molds.map(mold => mold.plate).filter(Boolean))],
      getFilterValue: (mold) => mold.plate || '',
      toCSV: (mold) => mold.plate || ''
    },
    {
      key: 'gateType',
      label: 'Gate Type',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{mold.gateType ?? '-'}</div>,
      filterOptions: (molds) => [...new Set(molds.map(mold => mold.gateType).filter(Boolean))],
      getFilterValue: (mold) => mold.gateType ?? '',
      toCSV: (mold) => mold.gateType ?? ''
    },
    {
      key: 'delayGate',
      label: 'Delay Gate',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{mold.delayGate ? 'ใช่' : 'ไม่'}</div>,
      filterOptions: () => ['ใช่', 'ไม่'],
      getFilterValue: (mold) => mold.delayGate ? 'ใช่' : 'ไม่',
      toCSV: (mold) => mold.delayGate ? 'ใช่' : 'ไม่'
    },
    {
      key: 'hotRunner',
      label: 'Hot Runner',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{mold.hotRunner ? 'ใช่' : 'ไม่'}</div>,
      filterOptions: () => ['ใช่', 'ไม่'],
      getFilterValue: (mold) => mold.hotRunner ? 'ใช่' : 'ไม่',
      toCSV: (mold) => mold.hotRunner ? 'ใช่' : 'ไม่'
    },
    {
      key: 'hotRunnerType',
      label: 'Hot Runner Type',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{mold.hotRunnerType ?? '-'}</div>,
      filterOptions: (molds) => [...new Set(molds.map(mold => mold.hotRunnerType).filter(Boolean))],
      getFilterValue: (mold) => mold.hotRunnerType ?? '',
      toCSV: (mold) => mold.hotRunnerType ?? ''
    },
    {
      key: 'hotRunnerBrand',
      label: 'Hot Runner Brand',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{mold.hotRunnerBrand ?? '-'}</div>,
      filterOptions: (molds) => [...new Set(molds.map(mold => mold.hotRunnerBrand).filter(Boolean))],
      getFilterValue: (mold) => mold.hotRunnerBrand ?? '',
      toCSV: (mold) => mold.hotRunnerBrand ?? ''
    },
    {
      key: 'qtyZoneHr',
      label: "Q'ty Zone HR",
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: false,
      render: (mold) => <div>{mold.qtyZoneHr ?? '-'}</div>,
      getFilterValue: (mold) => mold.qtyZoneHr ?? '',
      toCSV: (mold) => mold.qtyZoneHr ?? ''
    },
    {
      key: 'partMatGrade',
      label: 'Part Material Grade',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{(mold as any).partMatGrade ?? '-'}</div>,
      filterOptions: (molds) => [...new Set(molds.map(mold => (mold as any).partMatGrade).filter(Boolean))],
      getFilterValue: (mold) => (mold as any).partMatGrade ?? '',
      toCSV: (mold) => (mold as any).partMatGrade ?? ''
    },
    {
      key: 'surfaceFinish',
      label: 'Surface Finish',
      group: 'ข้อมูลทั่วไป',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{(mold as any).surfaceFinish ?? '-'}</div>,
      filterOptions: (molds) => [...new Set(molds.map(mold => (mold as any).surfaceFinish).filter(Boolean))],
      getFilterValue: (mold) => (mold as any).surfaceFinish ?? '',
      toCSV: (mold) => (mold as any).surfaceFinish ?? ''
    },
    {
      key: 'remarks',
      label: 'จุดที่ต้องระวัง',
      group: 'ข้อมูลทั่วไป',
      sortable: false,
      filterable: false,
      render: (mold) => <div className="max-w-[200px] truncate">{mold.remarks ?? '-'}</div>,
      toCSV: (mold) => `"${(mold.remarks ?? '').replace(/"/g, '""')}"`
    },
    // ── จำนวนชอต ──────────────────────────────────────────────────────
    {
      key: 'shotCount',
      label: '#Shot',
      group: 'จำนวนชอต',
      sortable: false,
      filterable: false,
      render: (mold) => (
        <MoldShotProgressGradient
          shotCount={mold.cycleShotCount}
          servicingAlertShotThreshold={mold.servicingAlertShotThreshold}
          servicingMaxShotThreshold={mold.servicingMaxShotThreshold}
          maxShotCount={mold.maxShotCount}
        />
      ),
    },
    {
      key: 'cycleShotCountDisplay',
      label: 'ชอตสะสมจากระบบในรอบล้าง',
      group: 'จำนวนชอต',
      sortable: true,
      filterable: false,
      render: (mold) => <div>{mold.cycleShotCount.toLocaleString()}</div>,
      getFilterValue: (mold) => mold.cycleShotCount?.toString() ?? '0',
      toCSV: (mold) => mold.cycleShotCount?.toString() ?? '0'
    },
    {
      key: 'shotCountDisplay',
      label: 'ชอตสะสมจากระบบ',
      group: 'จำนวนชอต',
      sortable: true,
      filterable: false,
      render: (mold) => <div>{mold.shotCount.toLocaleString()}</div>,
      getFilterValue: (mold) => mold.shotCount?.toString() ?? '0',
      toCSV: (mold) => mold.shotCount?.toString() ?? '0'
    },
    {
      key: 'physicalShotCountDisplay',
      label: 'ชอตสะสมของเคาน์เตอร์แม่พิมพ์',
      group: 'จำนวนชอต',
      sortable: true,
      filterable: false,
      render: (mold) => <div>{mold.physicalShotCount.toLocaleString()}</div>,
      getFilterValue: (mold) => mold.physicalShotCount?.toString() ?? '0',
      toCSV: (mold) => mold.physicalShotCount?.toString() ?? '0'
    },
    {
      key: 'servicingAlertShotThreshold',
      label: 'ชอตเตือนเซอร์วิส',
      group: 'จำนวนชอต',
      sortable: true,
      filterable: false,
      render: (mold) => <div>{mold.servicingAlertShotThreshold.toLocaleString()}</div>,
      getFilterValue: (mold) => mold.servicingAlertShotThreshold.toString(),
      toCSV: (mold) => mold.servicingAlertShotThreshold.toString()
    },
    {
      key: 'servicingMaxShotThreshold',
      label: 'ชอตที่ต้องเซอร์วิส',
      group: 'จำนวนชอต',
      sortable: true,
      filterable: false,
      render: (mold) => <div>{mold.servicingMaxShotThreshold.toLocaleString()}</div>,
      getFilterValue: (mold) => mold.servicingMaxShotThreshold.toString(),
      toCSV: (mold) => mold.servicingMaxShotThreshold.toString()
    },
    {
      key: 'maxShotCount',
      label: 'Mold การันตี',
      group: 'จำนวนชอต',
      sortable: true,
      filterable: false,
      render: (mold) => <div>{mold.maxShotCount.toLocaleString()}</div>,
      getFilterValue: (mold) => mold.maxShotCount?.toString() ?? '0',
      toCSV: (mold) => mold.maxShotCount?.toString() ?? '0'
    },
    // ── ขนาด ──────────────────────────────────────────────────────────
    {
      key: 'width',
      label: 'Width',
      group: 'ขนาด',
      sortable: true,
      filterable: false,
      render: (mold) => <div>{mold.width ?? '-'}</div>,
      getFilterValue: (mold) => mold.width?.toString() ?? '',
      toCSV: (mold) => mold.width?.toString() ?? ''
    },
    {
      key: 'height',
      label: 'Height',
      group: 'ขนาด',
      sortable: true,
      filterable: false,
      render: (mold) => <div>{mold.height ?? '-'}</div>,
      getFilterValue: (mold) => mold.height?.toString() ?? '',
      toCSV: (mold) => mold.height?.toString() ?? ''
    },
    {
      key: 'length',
      label: 'Length',
      group: 'ขนาด',
      sortable: true,
      filterable: false,
      render: (mold) => <div>{mold.length ?? '-'}</div>,
      getFilterValue: (mold) => mold.length?.toString() ?? '',
      toCSV: (mold) => mold.length?.toString() ?? ''
    },
    {
      key: 'weight',
      label: 'Mold Weight',
      group: 'ขนาด',
      sortable: true,
      filterable: false,
      render: (mold) => <div>{mold.weight ?? '-'}</div>,
      getFilterValue: (mold) => mold.weight?.toString() ?? '',
      toCSV: (mold) => mold.weight?.toString() ?? ''
    },
    {
      key: 'machineSizeMin',
      label: 'MC Size Min',
      group: 'ขนาด',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{mold.machineSizeMin ?? '-'}</div>,
      filterOptions: (molds) => [...new Set(molds.map(mold => mold.machineSizeMin?.toString()).filter(Boolean))],
      getFilterValue: (mold) => mold.machineSizeMin?.toString() ?? '',
      toCSV: (mold) => mold.machineSizeMin?.toString() ?? ''
    },
    {
      key: 'machineSizeMax',
      label: 'MC Size Max',
      group: 'ขนาด',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{mold.machineSizeMax ?? '-'}</div>,
      filterOptions: (molds) => [...new Set(molds.map(mold => mold.machineSizeMax?.toString()).filter(Boolean))],
      getFilterValue: (mold) => mold.machineSizeMax?.toString() ?? '',
      toCSV: (mold) => mold.machineSizeMax?.toString() ?? ''
    },
    {
      key: 'locatedRingOd',
      label: 'Located Ring O.D.',
      group: 'ขนาด',
      sortable: true,
      filterable: false,
      render: (mold) => <div>{mold.locatedRingOd ?? '-'}</div>,
      getFilterValue: (mold) => mold.locatedRingOd?.toString() ?? '',
      toCSV: (mold) => mold.locatedRingOd?.toString() ?? ''
    },
    {
      key: 'nozzleSr',
      label: 'Nozzle SR',
      group: 'ขนาด',
      sortable: true,
      filterable: false,
      render: (mold) => <div>{mold.nozzleSr ?? '-'}</div>,
      getFilterValue: (mold) => mold.nozzleSr?.toString() ?? '',
      toCSV: (mold) => mold.nozzleSr?.toString() ?? ''
    },
    // ── Insert ────────────────────────────────────────────────────────
    {
      key: 'insertEngraving',
      label: 'Insert/Engraving',
      group: 'Insert',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{mold.insertEngraving ? 'ใช่' : 'ไม่'}</div>,
      filterOptions: () => ['ใช่', 'ไม่'],
      getFilterValue: (mold) => mold.insertEngraving ? 'ใช่' : 'ไม่',
      toCSV: (mold) => mold.insertEngraving ? 'ใช่' : 'ไม่'
    },
    {
      key: 'insertChangeType',
      label: 'ลักษณะการเปลี่ยน Insert',
      group: 'Insert',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{mold.insertChangeType ?? '-'}</div>,
      filterOptions: (molds) => [...new Set(molds.map(mold => mold.insertChangeType).filter(Boolean))],
      getFilterValue: (mold) => mold.insertChangeType ?? '',
      toCSV: (mold) => mold.insertChangeType ?? ''
    },
    // ── ที่ตั้ง ────────────────────────────────────────────────────────
    {
      key: 'currentLocation',
      label: 'ที่อยู่แม่พิมพ์',
      group: 'ที่ตั้ง',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{mold.currentLocation ?? '-'}</div>,
      filterOptions: (molds) => [...new Set(molds.map(mold => mold.currentLocation).filter(Boolean))],
      getFilterValue: (mold) => mold.currentLocation ?? '',
      toCSV: (mold) => mold.currentLocation ?? ''
    },
    {
      key: 'storageLocation',
      label: 'ที่เก็บ',
      group: 'ที่ตั้ง',
      sortable: true,
      filterable: true,
      render: (mold) => <div>{mold.storageLocation ?? '-'}</div>,
      filterOptions: (molds) => [...new Set(molds.map(mold => mold.storageLocation).filter(Boolean))],
      getFilterValue: (mold) => mold.storageLocation ?? '',
      toCSV: (mold) => mold.storageLocation ?? ''
    },
    {
      key: 'assetNoCustomer',
      label: 'Asset NO Customer',
      group: 'ที่ตั้ง',
      sortable: true,
      filterable: false,
      render: (mold) => <div>{mold.assetNoCustomer ?? '-'}</div>,
      getFilterValue: (mold) => mold.assetNoCustomer ?? '',
      toCSV: (mold) => mold.assetNoCustomer ?? ''
    },
    // ── Actions ───────────────────────────────────────────────────────
    {
      key: 'actions',
      label: 'Actions',
      group: 'Actions',
      sortable: false,
      filterable: false,
      render: (mold: Mold) => <ActionCell id={mold.id} prefixUrl={"molds"} suffixUrl={"?source=mold_list"}/>,
    },
  ];

  // Apply company-specific column configuration if companyCode is provided
  if (companyCode) {
    return applyMoldColumnConfiguration(allColumns, companyCode);
  }

  return allColumns;
}

export const moldTableColumns: GenericColumn<Mold>[] = getMoldTableColumns();

export const MoldTableView: React.FC<MoldTableViewProps> = ({
  molds,
  visibleColumns,
  selectedMolds,
  onSelect,
  onSelectAll,
  pagination = false,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onDataChange,
}) => {
  const { getMoldStatusLabel, companyCode } = useAppState();
  const navigate = useNavigate();
  const isSelected = (mold: Mold) => selectedMolds.includes(mold.id);

  const isDisabled = (mold: Mold) => !!mold.openMoldOrder;

  const selectMold = (mold: Mold) => !isDisabled(mold) && onSelect(mold.id);

  const moldTableColumns: GenericColumn<Mold>[] = getMoldTableColumns({
    getStatusText: getMoldStatusLabel,
    companyCode
  });
  const filteredColumns = moldTableColumns.filter(column => visibleColumns[column.key]);

  return (
    <TableComponent<Mold>
      data={molds}
      columns={filteredColumns}
      selectableRows={true}
      selectedRows={isSelected}
      disableRow={isDisabled}
      onSelectAllRows={onSelectAll}
      onRowSelect={selectMold}
      getRowHref={(mold) => `/molds/${mold.id}?source=mold_list`}
      onRowClick={(mold, event) => {
        const href = `/molds/${mold.id}?source=mold_list`;
        if (event?.ctrlKey || event?.metaKey || event?.shiftKey) {
          window.open(href, '_blank', 'noopener,noreferrer');
        } else {
          navigate(href);
        }
      }}
      onDataChange={onDataChange}
      DisabledRowComponent={({ row }) => <DisabledMoldTableRow mold={row} />}
      pagination={pagination}
      // Use parent component's pagination controls
      externalPagination={{
        currentPage,
        pageSize,
        onPageChange,
        onPageSizeChange,
      }}
      savePreferenceSettings={{
        isEnable: true,
        key: 'moldTable',
      }}
    />
  );
};

const DisabledMoldTableRow = ({ mold }: { mold: Mold }) => {
  const navigate = useNavigate();
  const { getMoldDisplayFields } = useAppState();
  const secondaryField = getMoldDisplayFields().secondaryField;
  const href = `/molds/${mold.id}?source=mold_list`;
  const open = (event: React.MouseEvent<HTMLTableRowElement>) => {
    if (event.ctrlKey || event.metaKey || event.shiftKey) window.open(href, '_blank', 'noopener,noreferrer');
    else navigate(href);
  };

  return (
    <tr
      className="bg-gray-100 opacity-70 cursor-pointer hover:bg-gray-200/70"
      onClick={open}
      onAuxClick={(event) => event.button === 1 && window.open(href, '_blank', 'noopener,noreferrer')}
      role="link"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          navigate(href);
        }
      }}
    >
      <td className="px-6 py-4 whitespace-nowrap" onClick={(event) => event.stopPropagation()}>
        <SelectCell isSelected={false} disabled={true} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <a href={href} className="text-base text-gray-900 font-medium" onClick={(event) => event.stopPropagation()}>
          {mold.sbiId}
        </a>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <TableCell mold={mold} column={{ key: secondaryField ?? 'productParts', header: '', label: '' }} selectedMolds={[]} />
      </td>
      <td colSpan={4} className="px-6 py-4 whitespace-nowrap text-center text-red-600 text-base font-medium">
        ไม่สามารถเปิดใบงานใหม่ได้เนื่องจากมีใบงานอยู่ในระหว่างดำเนินการ
      </td>
    </tr>
  );
};
